/**
 * Catalog options for the estimator's reference dropdowns (design §9).
 *
 * Fetches ACTIVE rows only, so a deactivated item disappears from the picker for new lines while a
 * line already using it keeps the rate frozen onto it. Results are cached per catalog for the
 * session: the estimator renders one picker per line item, and each must not trigger its own GET.
 * Admin mutations must call `invalidateCatalogOptions` or the picker stays stale until a reload.
 *
 * SCOPE (P5): a BID's line-item pickers no longer use this — they read that bid's frozen rate card
 * via `useRateCardOptions`, per design §9, so editing an old bid cannot freeze today's rate onto it.
 * This module remains the source for catalog-wide pickers that are not bid-scoped: the admin
 * screens' `catalogRef` fields, and `useRateCardOptions`' own fallback for a pre-P5 bid that has
 * no card.
 */
import { useEffect, useState } from 'react';
import { catalogByKey } from './catalogDescriptors';
import { listCatalog } from './referenceDataService';

export interface CatalogOption {
  id: string;
  /** What the picker shows — the catalog's natural key. */
  label: string;
  /** Rate to freeze onto the line when picked (undefined for catalogs without one). */
  rate?: number;
  /** The row's category (the MSA's type axis, nullable in the catalog) — read only by pickers
   * that group by type (Build C, plan mob-loads-categories-and-type-slots). */
  category?: string;
}

const cache = new Map<string, CatalogOption[]>();
const inflight = new Map<string, Promise<CatalogOption[]>>();

/** Which column supplies the rate a pick freezes onto the line. */
const RATE_FIELD: Record<string, string> = {
  equipment: 'default_isr',
  labour: 'default_rate',
};

async function fetchOptions(key: string): Promise<CatalogOption[]> {
  const descriptor = catalogByKey(key);
  if (!descriptor) return [];
  const rateField = RATE_FIELD[key];
  const rows = await listCatalog(descriptor.url, true);
  return rows.map((row) => {
    // default_isr/default_rate are NULLABLE. Number(null) is 0, which would freeze a $0 rate onto
    // the line and wipe a hand-typed one — so a missing rate must stay `undefined`, not become 0.
    const cell = rateField ? row[rateField] : null;
    const raw = cell === null || cell === undefined || cell === '' ? NaN : Number(cell);
    return {
      id: row.id,
      label: String(row[descriptor.keyField] ?? row.id),
      rate: Number.isFinite(raw) ? raw : undefined,
      // Nullable and possibly blank in the catalog — a missing category stays undefined so
      // type-grouping pickers can filter on truthiness.
      category: row.category ? String(row.category) : undefined,
    };
  });
}

/** Shared loader so N pickers mounting at once issue ONE request per catalog. */
export function loadCatalogOptions(key: string): Promise<CatalogOption[]> {
  const cached = cache.get(key);
  if (cached) return Promise.resolve(cached);

  const pending = inflight.get(key);
  if (pending) return pending;

  const promise = fetchOptions(key)
    .then((options) => {
      cache.set(key, options);
      return options;
    })
    .catch(() => [] as CatalogOption[]) // a catalog outage must not break the estimator
    .finally(() => inflight.delete(key));

  inflight.set(key, promise);
  return promise;
}

/** Drop the cache so a catalog edit is picked up without a reload (used by the admin screens). */
export function invalidateCatalogOptions(key?: string): void {
  if (key) cache.delete(key);
  else cache.clear();
}

export function useCatalogOptions(key: string): CatalogOption[] {
  const [options, setOptions] = useState<CatalogOption[]>(() => cache.get(key) ?? []);

  useEffect(() => {
    let cancelled = false;
    void loadCatalogOptions(key).then((loaded) => {
      if (!cancelled) setOptions(loaded);
    });
    return () => {
      cancelled = true;
    };
  }, [key]);

  return options;
}
